import { useInput, updateWidgetPropertyByPath, useAppDispatch } from '@libs/core';
import { JsonPathEnum, TCommonInputOption, TInputMethods, TInputData, TWidget } from '@libs/types';
import { useCallback, useEffect, useRef, FocusEvent } from 'react';
import { TDateInputData } from '../types/data';
import { validateDate } from './dateUtils';

export const useDateInput = (widget: TWidget<TDateInputData, TCommonInputOption>, methods: TInputMethods) => {
    const {
        uid,
        internalId,
        data: { mandatory, regex, mask, validationsStates },
        options = {},
    } = widget;
    const { isDisabled, isHidden, triggerActionsByType } = methods;
    const isFirstFocusRender = useRef(!isDisabled);
    const dispatch = useAppDispatch();

    const {
        value,
        isFocused,
        isFilled,
        isError,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        setError,
        setErrorMessage,
        handleChange: coreHandleChange,
        handleBlur: coreHandleBlur,
        handleFocus: coreHandleFocus,
    } = useInput(widget as TWidget<TInputData, TCommonInputOption>, { isDisabled, isHidden, triggerActionsByType }, { skipValidation: true });

    useEffect(() => {
        if (internalId) {
            dispatch(
                updateWidgetPropertyByPath({
                    uid: uid,
                    value: validateDate(value, mask, mandatory, regex, defaultErrorMessage, validationsStates).isValid,
                    path: JsonPathEnum.IS_VALID,
                }),
            );
        }
    }, [dispatch, defaultErrorMessage, internalId, uid, validationsStates, value, mandatory, regex, mask]);

    const handleChange = useCallback(
        (val: string) => {
            setError(false);
            coreHandleChange?.(undefined, val);
        },
        [coreHandleChange, setError],
    );

    const handleBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            coreHandleBlur?.(e);
            if (!isDisabled) {
                const validationResult = validateDate(e?.target?.value ?? '', mask, mandatory, regex, defaultErrorMessage, validationsStates);
                setError(!validationResult.isValid);
                setErrorMessage(validationResult.errorMessage);
            }
        },
        [coreHandleBlur, isDisabled, mask, mandatory, regex, defaultErrorMessage, validationsStates, setError, setErrorMessage],
    );

    const handleFocus = useCallback(
        (e: FocusEvent<Element>) => {
            coreHandleFocus?.(e);
            if (!isDisabled) {
                setError(Boolean(isFirstFocusRender.current && options.isError && options.onFocus));
                isFirstFocusRender.current = false;
            }
        },
        [coreHandleFocus, isDisabled, options.isError, options.onFocus, setError],
    );

    return {
        value,
        isFocused,
        isFilled,
        isError,
        errorMessage,
        label,
        helperText,
        handleChange,
        handleBlur,
        handleFocus,
    };
};
